import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import './MyRentals.scss';
import api from '../Axios/axios';
import { FaPencilAlt, FaArrowRight } from 'react-icons/fa';

const MyRentals = () => {
  const navigate = useNavigate();

  const [towerCranes, setTowerCranes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [rentals, setRentals] = useState([]);

  const [userCompanyId, setUserCompanyId] = useState(null);
  const [userRole, setUserRole] = useState('');

  // (İleride filtre UI’si eklersen hazır dursun)
  const [filters, setFilters] = useState({
    serialNumber: '',
    brand: '',
    model: '',
    height: '',
    tonnage: '',
    status: 'all', // 'all' | 'Inused' | 'Finished'
    workSite: '',
  });

  // ---- helpers ----
  const getUserFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const companyId = decodedToken.CompanyId;
      const role =
        decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      setUserCompanyId(companyId);
      setUserRole(role);
    } catch (e) {
      console.error('Token çözümlenemedi:', e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return new Intl.NumberFormat('tr-TR').format(num) + ' TL';
  };

  const getBrandName = (brandId) => {
    const b = brands.find((x) => x.id === brandId);
    return b ? b.name : '-';
  };

  // ---- data ----
  const fetchAll = async () => {
    try {
      const [cranesRes, brandsRes, rentalsRes] = await Promise.all([
        api.get('/TowerCrane'),
        api.get('/Brand'),
        api.get('/Rental', { params: { includeLineItems: false } }),
      ]);
      setTowerCranes(cranesRes.data?.data ?? []);
      setBrands(brandsRes.data?.data ?? []);
      setRentals(rentalsRes.data?.data ?? []);
    } catch (e) {
      console.error('Veriler yüklenemedi:', e);
    }
  };

  useEffect(() => {
    getUserFromToken();
  }, []);

  useEffect(() => {
    fetchAll();
  }, [userCompanyId]);

  // Şirketin yaptığı TÜM kiralamaları genişlet (aktif + bitmiş)
  const companyRentalsExpanded = useMemo(() => {
    if (!userCompanyId) return [];
    return rentals
      .filter((r) => r.rentingCompanyId === userCompanyId)
      .map((r) => {
        const c = towerCranes.find((tc) => tc.id === r.towerCraneId);
        return {
          key: r.id,
          craneId: c?.id ?? null,
          serialNo: c?.serialNo ?? '-',
          brandId: c?.brandId ?? null,
          brandName: c ? getBrandName(c.brandId) : '-',
          model: c?.model ?? '-',
          height: c?.height ?? '-',
          tonnage: c?.maxTonnage ?? '-',
          status: r.isActive ? 'Inused' : 'Finished',
          startDate: formatDate(r.rentalStartDate),
          endDate: formatDate(r.rentalEndDate),
          fee: formatCurrency(r.rent),
          rentalDuration: r.rentalPeriod || '-',
          workSite: r.workSite || '-',
          rentalId: r.id,
          _startRaw: r.rentalStartDate,
          _endRaw: r.rentalEndDate,
        };
      });
  }, [rentals, towerCranes, userCompanyId, brands]);

  // Filtreler
  const filteredRows = useMemo(() => {
    return companyRentalsExpanded
      .filter((row) => {
        const matchesSerial =
          !filters.serialNumber ||
          (row.serialNo || '').toLowerCase().includes(filters.serialNumber.toLowerCase());

        const matchesBrand =
          !filters.brand || String(row.brandId) === String(filters.brand);

        const matchesModel = !filters.model || row.model === filters.model;

        const matchesHeight =
          !filters.height || String(row.height) === String(filters.height);

        const matchesTon =
          !filters.tonnage || String(row.tonnage) === String(filters.tonnage);

        const matchesStatus =
          filters.status === 'all' || row.status === filters.status;

        const matchesSite = !filters.workSite || row.workSite === filters.workSite;

        return (
          matchesSerial &&
          matchesBrand &&
          matchesModel &&
          matchesHeight &&
          matchesTon &&
          matchesStatus &&
          matchesSite
        );
      })
      // Aktifler öne, sonra bitmişler; bitmişleri yakın tarihten eskiye sırala
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === 'Inused' ? -1 : 1;
        const aEnd = a._endRaw ? new Date(a._endRaw).getTime() : 0;
        const bEnd = b._endRaw ? new Date(b._endRaw).getTime() : 0;
        return bEnd - aEnd;
      });
  }, [companyRentalsExpanded, filters]);

  // ---- actions ----
  const handleEdit = (rentalId) => navigate(`/rentaledit/${rentalId}`);
  const handleRentalPage = (craneId) => craneId && navigate(`/rentalpage/${craneId}`);

  const getRowClass = (status) => (status === 'Inused' ? 'row-used' : 'row-finished');

  return (
    <div className="my-rentals">
      <Navbar />

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="index-col">#</th>
              <th>Seri Numarası</th>
              <th>Marka</th>
              <th>Model</th>
              <th>Yükseklik</th>
              <th>Tonaj</th>
              <th>Durum</th>
              <th>Kira Başlangıç Tarihi</th>
              <th>Kira Bitiş Tarihi</th>
              <th>Ücret</th>
              <th>Kira Süresi (Ay)</th>
              <th>Şantiye/Depo</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((row, index) => (
              <tr key={row.key} className={getRowClass(row.status)}>
                <td className="index-col">{index + 1}</td>
                <td>{row.serialNo}</td>
                <td>{row.brandName}</td>
                <td>{row.model}</td>
                <td>{row.height}m</td>
                <td>{row.tonnage}T</td>
                <td>{row.status === 'Inused' ? 'Kirada' : 'Bitti'}</td>
                <td>{row.startDate}</td>
                <td>{row.endDate}</td>
                <td>{row.fee}</td>
                <td>{row.rentalDuration}</td>
                <td>{row.workSite}</td>

              </tr>
            ))}

            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={13} className="empty-cell">
                  Kayıt bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyRentals;
